// src/components/BackToTopFab.jsx
//
// Floating "scroll to top" FAB that appears once the user has scrolled
// past a threshold. Listens on the nearest scrollable ancestor (the
// Layout's main content Box), falling back to window scroll.
// Positioned above the mobile bottom nav so it never overlaps.

import React, { useEffect, useState } from 'react';
import { Fab, Zoom } from '@mui/material';
import { KeyboardArrowUp as UpIcon } from '@mui/icons-material';
import { BOTTOM_NAV_HEIGHT } from './MobileBottomNav';

export const BACK_TO_TOP_THRESHOLD = 400;
export const BACK_TO_TOP_TARGET = '#main-content';

/**
 * Whether the back-to-top FAB is showing. Exported (20260925-ui-feedback, K-9) so the
 * UI-feedback FAB can sit ABOVE it when it is visible and take its place when it is not,
 * from the same threshold — two copies of the rule would drift and the FABs would overlap.
 */
export function useBackToTopVisible({
  threshold = BACK_TO_TOP_THRESHOLD,
  targetSelector = BACK_TO_TOP_TARGET,
} = {}) {
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const el =
      (targetSelector && document.querySelector(targetSelector)) || window;
    const getY = () =>
      el === window ? window.scrollY || window.pageYOffset : el.scrollTop;
    const onScroll = () => setVisible(getY() > threshold);
    onScroll();
    el.addEventListener('scroll', onScroll, { passive: true });
    return () => el.removeEventListener('scroll', onScroll);
  }, [threshold, targetSelector]);

  return visible;
}

const BackToTopFab = ({ threshold = BACK_TO_TOP_THRESHOLD, targetSelector = BACK_TO_TOP_TARGET }) => {
  const visible = useBackToTopVisible({ threshold, targetSelector });

  const scrollToTop = () => {
    const el =
      (targetSelector && document.querySelector(targetSelector)) || window;
    if (el === window) {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } else {
      el.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  return (
    <Zoom in={visible} unmountOnExit>
      <Fab
        size="medium"
        color="primary"
        onClick={scrollToTop}
        aria-label="Back to top"
        sx={{
          position: 'fixed',
          right: 16,
          bottom: {
            xs: `calc(${BOTTOM_NAV_HEIGHT}px + env(safe-area-inset-bottom, 0px) + 16px)`,
            md: 24,
          },
          zIndex: (t) => t.zIndex.speedDial,
        }}
      >
        <UpIcon />
      </Fab>
    </Zoom>
  );
};

export default BackToTopFab;
